import {
  DotsThree,
  HandPointing,
  ImagesSquare,
  Textbox,
} from "@phosphor-icons/react";
import { actions } from "astro:actions";
import { useMemo } from "react";
import { match } from "ts-pattern";

import logo from "~/assets/logo.svg?url";
import { Avatar } from "~/components/Avatar";
import { ForkPrimary } from "~/components/ForkPrimary";
import { GAME_MODES } from "~/components/Game";
import { useGame } from "~/components/GameContext";
import { Menu, MenuButton } from "~/components/Menu";
import { PackActions } from "~/components/PackActions";
import { PackCoverImage } from "~/components/PackCoverImage";
import { PreGame } from "~/components/PreGame";
import { useLocale } from "~/hooks/locale";
import type { FragmentType } from "~/utils/fragment";
import { getUserDisplayName } from "~/utils/user";

import type { PackPreGame_PackFragment } from "./PackPreGame_PackFragment";

export const PackPreGame: React.FC<{
  pack: FragmentType<"Pack", { select: typeof PackPreGame_PackFragment }>;
}> = ({ pack }) => {
  const { t } = useLocale();
  const { game, setGame } = useGame();

  const getPackWords = useMemo(
    () =>
      pack.words.length > 0
        ? () =>
            actions.game.getPackWords.orThrow({
              packId: pack.id,
            })
        : undefined,
    [pack.id, pack.words.length],
  );

  const hasImages = useMemo(
    () =>
      pack.words.some(({ word }) =>
        word.definitions.some((definition) => definition.image),
      ),
    [pack.words],
  );

  return (
    <PreGame
      eventName="pack_started"
      minTimeLimit={0}
      queryFn={getPackWords}
      title={
        <>
          <div className="flex items-end gap-4 md:gap-6">
            {pack.image && (
              <div className="relative size-32 shrink-0 overflow-hidden rounded shadow-sm max-sm:hidden md:size-40">
                <PackCoverImage image={pack.image} color={pack.color} />
              </div>
            )}
            <div className="relative space-y-2 md:space-y-4">
              <div className="space-y-1 md:space-y-2">
                {pack.collection && (
                  <div className="title title-sm">{pack.collection.name}</div>
                )}
                <h1 className="title title-xl">{pack.name}</h1>
              </div>
              <div className="flex items-center gap-2 md:text-lg">
                {pack.original ? (
                  <>
                    <img src={logo} alt="Knoword logo" className="size-5" />
                    <span>Knoword</span>
                  </>
                ) : (
                  <>
                    <Avatar user={pack.user} />
                    <span>{getUserDisplayName(pack.user)}</span>
                  </>
                )}
                &middot;
                <span>
                  {pack.words.length}{" "}
                  {t(pack.words.length > 1 ? "terms_plural" : "term_singular")}
                </span>
              </div>
            </div>
          </div>
          <div className="absolute right-2 top-2">
            <Menu>
              <MenuButton className="btn btn-circle max-lg:btn-sm">
                <DotsThree weight="bold" className="size-6 lg:size-8" />
              </MenuButton>
              <PackActions pack={pack} omit={["delete"]} />
            </Menu>
          </div>
        </>
      }
    >
      {GAME_MODES.filter((mode) => mode !== "imcq" || hasImages).map(
        (mode, index) => (
          <ForkPrimary
            key={index}
            icon={match(mode)
              .with("classic", () => Textbox)
              .with("mcq", () => HandPointing)
              .with("imcq", () => ImagesSquare)
              .exhaustive()}
            title={t(mode)}
            description={t(`${mode}_description`)}
            aria-selected={mode === game.mode}
            onClick={() => {
              setGame((prev) => ({
                ...prev,
                mode,
              }));
            }}
          />
        ),
      )}
    </PreGame>
  );
};
